import { State } from 'redux/initialState';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import actionTypes from 'constants/actionTypes';
import { formatDateToInput } from 'constants/formats';
import menuTypes from 'constants/menuTypes';

import { useGetActualSociety } from 'hooks/useGetActualSociety';

import { useGetHoldingClassOptions } from 'modules/partners/hooks/useGetHoldingClassesOption';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetPartnerOptions } from 'modules/partners/hooks/useGetPartnerOptions';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import { store } from 'redux/store';
import { setModal } from 'modules/_shared/redux/modalActions';
import PublishTradingOfferModal from 'modules/trading/modals/PublishTradingOffer';
import { PublishTradingOfferModalProvider } from 'modules/trading/modals/PublishTradingOffer/context/PublishTradingOfferModalContext';
import { mapPartnerOptionToPartialPartner } from 'modules/trading/modals/PublishTradingOffer/utils/buildInitialPublisherPartner';
import { TradingOffer, TradingOfferType } from 'types/TradingOffer';
import { SocietyEquityMarketStatus } from 'types/Society';
import { AddTradingOfferPageContextType } from '../types/AddTradingOfferPageContextType';
import { addTradingOfferPageInitValue } from '../constants/addTradingOfferPageInitValue';

import { useGetPrices } from '../hooks/useGetPrices';
import { useGetMaxSharesToTransfer } from '../hooks/useGetMaxSharesToTransfer';
import { useGetPartnerCurrentSharesCount } from '../hooks/useGetPartnerCurrentSharesCount';

export const AddTradingOfferPageContext =
  createContext<AddTradingOfferPageContextType>(addTradingOfferPageInitValue);

type AddTradingOfferPageProviderProps = {
  children: ReactNode;
  tradingOfferType: TradingOfferType;
  actionType?: string;
};

export const AddTradingOfferPageProvider: FC<
  AddTradingOfferPageProviderProps
> = ({ children, tradingOfferType, actionType = actionTypes.ADD }) => {
  const {
    societyId,
    partnerId: partnerIdParam,
    tradingOfferId,
  } = useParams<{
    societyId: string;
    partnerId: string;
    tradingOfferId: string;
  }>();
  const partnerIdQuery = new URLSearchParams(useLocation().search).get(
    'partnerId'
  );
  const partnerId = partnerIdParam || partnerIdQuery || '';

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const [selectedPartner, setSelectedPartner] = useState<string>(
    partnerId || ''
  );
  const [sharesToTransfer, setSharesToTransfer] = useState<number>(0);
  const [isSellAllMyShares, setIsSellAllMyShares] = useState<boolean>(false);
  const [isPartialAmountEnabled, setIsPartialAmountEnabled] =
    useState<boolean>(false);
  const [minSharesPerAcceptance, setMinSharesPerAcceptance] =
    useState<number>(1);
  const [additionalNotes, setAdditionalNotes] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [isPricePerShare, setIsPricePerShare] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: pricesData } = useGetPrices({
    price,
    sharesToTransfer,
    isPricePerShare,
  });

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptions({
      societyId,
      partnerId,
    });

  const { data: partnerCurrentSharesCountData } =
    useGetPartnerCurrentSharesCount({
      selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
    });

  const {
    data: holdingClassOptionsData,
    actions: holdingClassOptionsActions,
    isLoading: isLoadingHoldingClassOptions,
  } = useGetHoldingClassOptions({
    societyId,
    // holdingClassId: draftInvestorToEdit?.holdingClass,
    partnerId: tradingOfferType === 'SELL' ? selectedPartner : undefined,
  });

  const { data: maxSharesToTransferData } = useGetMaxSharesToTransfer({
    partnerActualSharesCount:
      partnerCurrentSharesCountData.partnerCurrentSharesCount,
    selectedHoldingClassOption:
      holdingClassOptionsData.selectedHoldingClassOption,
    tradingOfferType,
  });

  useEffect(() => {
    if (isSellAllMyShares && maxSharesToTransferData.maxSharesToTransfer > 0) {
      setSharesToTransfer(maxSharesToTransferData.maxSharesToTransfer);
    }
  }, [isSellAllMyShares, maxSharesToTransferData.maxSharesToTransfer]);

  const handleChangeSharesToTransfer = (value: number) => {
    setSharesToTransfer(value);
    if (value !== maxSharesToTransferData.maxSharesToTransfer) {
      setIsSellAllMyShares(false);
    }
  };

  const handleChangeIsSellAllMyShares = () => {
    const newValue = !isSellAllMyShares;
    setIsSellAllMyShares(newValue);
    setSharesToTransfer(
      newValue ? maxSharesToTransferData.maxSharesToTransfer : 0
    );
  };

  const handleChangeIsPartialAmountEnabled = () => {
    setIsPartialAmountEnabled((prev) => !prev);
  };

  const handleChangeMinSharesPerAcceptance = (value: number) => {
    setMinSharesPerAcceptance(value);
  };

  const handleChangeAdditionalNotes = (value: string) => {
    setAdditionalNotes(value);
  };

  const handleChangePrice = (value: number) => {
    setPrice(value);
  };

  const handleChangeIsPricePerShare = () => {
    setIsPricePerShare((prev) => !prev);
  };

  const handleChangeStartDate = (event: ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleChangeEndDate = (event: ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleSetDatePreset = (days: number) => {
    const today = new Date();
    const end = new Date();
    end.setDate(today.getDate() + days);

    setStartDate(formatDateToInput(today));
    setEndDate(formatDateToInput(end));
  };

  const handleChangePartner = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedPartner(selectedOption ? selectedOption.value : '');
    partnerOptionsActions.handleChangePartnerOption(selectedOption);
    setSharesToTransfer(0);
    setIsSellAllMyShares(false);
  };

  const handleChangeHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    holdingClassOptionsActions.handleChangeSelectedHoldingClass(selectedOption);
    setSharesToTransfer(0);
    setIsSellAllMyShares(false);
  };

  const handleReviewTradingOffer = () => {
    if (!societyId || !selectedPartner) return;

    const tradingOffer: Partial<TradingOffer> & {
      className: string;
      minSharesPerAcceptance?: number;
      additionalNotes?: string;
    } = {
      society: societyId,
      partner: selectedPartner,
      shareClass:
        (holdingClassOptionsData.selectedHoldingClassOption?.value as string) ||
        null,
      className:
        holdingClassOptionsData?.selectedHoldingClassOption?.name || '-',
      sharesCount: {
        total: sharesToTransfer,
        agreed: 0,
        approved: 0,
      },
      sharePrice: pricesData.pricePerShare,
      startDate,
      endDate,
      type: tradingOfferType,
      isPartialAmountEnabled,
      minSharesPerAcceptance: isPartialAmountEnabled
        ? minSharesPerAcceptance
        : undefined,
      additionalNotes: additionalNotes || undefined,
      documents: [],
    };

    store.dispatch(
      setModal(
        <PublishTradingOfferModalProvider
          tradingOffer={tradingOffer}
          modalActionType={actionType}
          size="lg"
          publisherPartner={mapPartnerOptionToPartialPartner(
            selectedPartner,
            partnerOptionsData.selectedPartnerOption
          )}
        >
          <PublishTradingOfferModal />
        </PublishTradingOfferModalProvider>
      )
    );
  };

  return (
    <AddTradingOfferPageContext.Provider
      value={{
        data: {
          tradingOfferType,
          actionType,
          partnerOptions: partnerOptionsData.partnerOptions || [],
          selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
          holdingClassOptions:
            holdingClassOptionsData.holdingClassOptions || [],
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          partnerCurrentSharesCount:
            partnerCurrentSharesCountData.partnerCurrentSharesCount,
          sharesToTransfer,
          maxSharesToTransfer: maxSharesToTransferData.maxSharesToTransfer,
          isSellAllMyShares,
          isPartialAmountEnabled,
          minSharesPerAcceptance,
          additionalNotes,
          price,
          pricePerShare: pricesData.pricePerShare,
          totalOfferPrice: pricesData.totalOfferPrice,
          societyId: societyId || '',
          societyName: actualSociety?.name || '',
          isPricePerShare,
          startDate,
          endDate,
          isPartnerView: !!partnerId,
          isAdmin,
          isReadOnly: false,
          isLoading: isLoading || !actualSociety,
          isMarketClosed:
            actualSociety?.configuration?.equityMarket?.status ===
              SocietyEquityMarketStatus.CLOSED ||
            actualSociety?.configuration?.equityMarket?.status ===
              SocietyEquityMarketStatus.UNAVAILABLE,
        },
        actions: {
          handleChangePartner,
          handleSearchPartnerByTerm:
            partnerOptionsActions.handleSearchPartnerByTerm,
          handleChangeHoldingClass,
          handleSearchHoldingClassByTerm:
            holdingClassOptionsActions.handleSearchHoldingClassByTerm,
          handleChangeSharesToTransfer,
          handleChangeIsSellAllMyShares,
          handleChangeIsPartialAmountEnabled,
          handleChangeMinSharesPerAcceptance,
          handleChangeAdditionalNotes,
          handleChangePrice,
          handleChangeIsPricePerShare,
          handleChangeStartDate,
          handleChangeEndDate,
          handleSetDatePreset,
          handleReviewTradingOffer,
        },
      }}
    >
      {children}
    </AddTradingOfferPageContext.Provider>
  );
};

export const useAddTradingOfferPage = () => {
  const context = useContext(AddTradingOfferPageContext);
  if (!context) {
    throw new Error(
      'useAddTradingOfferPage must be used within a AddTradingOfferPageProvider'
    );
  }
  return context;
};
